import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './EmailVerified.scss';
import Heading from '../../../common/heading/Heading';
import {
	ONBOARDING_EMAIL_VERIFIED_SUCCESS,
	ONBOARDING_EMAIL_VERIFIED_TEXT,
	ONBOARDING_EMAIL_VERIFIED_TITLE,
} from '../../../../constants/Copy';
import { stateRender } from '../../../../helpers/react';
import { getExternalUrl } from '../../../../config';
import { DEFAULT_RATEPLAN_SLUG } from '../../../../constants/Payment';
import {
	getTierRateplanFromUrl,
	isBundleIdInRedirectUrl,
	isHboBundleInRedirectUrl,
	isVoucherCodeInRedirectUrl,
} from '../../../../helpers/redirect';
import { EmailVerifiedProceedButton } from './EmailVerifiedProceedButton';
import EmailVerifiedTierDisplayLoadable from './EmailVerifiedTierDisplay.loadable';
import OnboardingProgress from '../../common/onboardingProgress/OnboardingProgress.container';
import { RATEPLAN_TIERID_MAP } from '../../../../constants/Tiers';
import useOnboardingTracking from '../../../../hooks/useOnboardingTracking';
import { ROUTE_TIER_OPTIONS } from '../../../../constants/Routes';

const EmailVerified = (props) => {
	const gigyaRedirectUrl = props.gigyaUser?.data?.redirectUrl;
	const gigyaUserData = props.gigyaUser?.data;
	const { dispatchFetchTiers, useHandleEmailVerification, useVideolandAccountState } = props;

	const isVoucherCodeInRedirect = isVoucherCodeInRedirectUrl(gigyaRedirectUrl);
	const isBundleInRedirect = isBundleIdInRedirectUrl(gigyaRedirectUrl);
	const isHboBundleInRedirect = isHboBundleInRedirectUrl(gigyaRedirectUrl);

	const tierRateplanFromRedirectUrl = getTierRateplanFromUrl(gigyaRedirectUrl);
	const tierId = RATEPLAN_TIERID_MAP[tierRateplanFromRedirectUrl];
	useOnboardingTracking({ tierId });

	const [isFetchTiersInitialized, setIsFetchTiersInitialized] = useState(false);
	const { isVerified } = useHandleEmailVerification();
	const { isBlackFriday, showPromo } = useVideolandAccountState();

	const shouldRedirectToAbonnementen =
		!!isVerified && !!gigyaUserData && !tierRateplanFromRedirectUrl && !isVoucherCodeInRedirect;

	useEffect(() => {
		if ((isBundleInRedirect || isHboBundleInRedirect) && !!isVerified && !!gigyaUserData) {
			window.location.href = getPreTieringRedirectUrlOrDefault(gigyaRedirectUrl);
			return;
		} else if (shouldRedirectToAbonnementen) {
			window.location.href = `${window.location.origin}${ROUTE_TIER_OPTIONS}`;
			return;
		}
		if (isVerified && gigyaUserData && !isFetchTiersInitialized && !isVoucherCodeInRedirect) {
			dispatchFetchTiers();
			setIsFetchTiersInitialized(true);
		}
	}, [
		shouldRedirectToAbonnementen,
		dispatchFetchTiers,
		isVerified,
		gigyaUserData,
		isVoucherCodeInRedirect,
		isFetchTiersInitialized,
		isBundleInRedirect,
		isHboBundleInRedirect,
		gigyaRedirectUrl,
	]);

	const { isTiersError, isTiersPending, toggleTieringSidebar } = props;

	return stateRender({
		isPending: !isVerified || isTiersPending || !gigyaUserData || shouldRedirectToAbonnementen,
		isError: isTiersError,
		onSuccess: renderContent,
		spinnerModifierClass: 'spinner--onboarding',
	});

	function renderContent() {
		const showTiering =
			!isVoucherCodeInRedirect && !!tierRateplanFromRedirectUrl && !isBundleInRedirect;

		return (
			<div className="onboarding-email-verified">
				<OnboardingProgress activeStep={3} />
				<Heading>
					{ONBOARDING_EMAIL_VERIFIED_SUCCESS}
					<br />
					{ONBOARDING_EMAIL_VERIFIED_TITLE}
				</Heading>
				{showTiering ? (
					<EmailVerifiedTierDisplayLoadable
						isBlackFriday={isBlackFriday}
						tiers={props.tierCollection}
						proceedUrl={gigyaRedirectUrl}
						selectedRateplanSlug={tierRateplanFromRedirectUrl}
						dispatchAnalyticsPush={props.dispatchAnalyticsPush}
						toggleTieringSidebar={toggleTieringSidebar}
						showPromo={showPromo}
					/>
				) : (
					<>
						<p className="onboarding__text" data-test="email-verified-proceed-title">
							{ONBOARDING_EMAIL_VERIFIED_TEXT}
						</p>
						<EmailVerifiedProceedButton
							proceedUrl={getPreTieringRedirectUrlOrDefault(gigyaRedirectUrl)}
							testId="email-verified-proceed-button"
						/>
					</>
				)}
			</div>
		);
	}

	function getPreTieringRedirectUrlOrDefault(gigyaRedirectUrl) {
		return (
			gigyaRedirectUrl || getExternalUrl('no_subscription', { rateplan: DEFAULT_RATEPLAN_SLUG })
		);
	}
};

EmailVerified.propTypes = {
	useHandleEmailVerification: PropTypes.func.isRequired,
	dispatchFetchTiers: PropTypes.func.isRequired,
	dispatchAnalyticsPush: PropTypes.func.isRequired,
	isTiersPending: PropTypes.bool.isRequired,
	isTiersError: PropTypes.bool.isRequired,
	tierCollection: PropTypes.arrayOf(PropTypes.object),
	gigyaUser: PropTypes.object.isRequired,
};

export default EmailVerified;
